"use client";

import { useForm, useWatch } from "react-hook-form";
import {
  HiSearch,
  HiExternalLink,
  HiClipboardCopy,
  HiFilter,
  HiQuestionMarkCircle,
} from "@/components/icons";
import { FaGoogle } from "@/components/icons";
import Link from "next/link";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

import { useToast } from "@/hooks/use-toast";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import { googleAdvancedSearchFAQ } from "@/lib/data/faq/google-advanced-search";

type GoogleSearchFormValues = {
  // Words
  allWords: string;
  exactPhrase: string;
  anyWords: string;
  noneWords: string;
  numbersFrom: string;
  numbersTo: string;

  // Narrow Results
  language: string;
  region: string;
  lastUpdate: string;
  siteOrDomain: string;
  termsAppearing: string;
  fileType: string;
};

export default function GoogleAdvancedSearchClient() {
  const { register, control } = useForm<GoogleSearchFormValues>();

  const values = useWatch({ control });
  const { toast } = useToast();

  const queryPreview = (() => {
    let queryParts: string[] = [];

    // Words
    if (values.allWords) queryParts.push(values.allWords);
    if (values.exactPhrase) queryParts.push(`"${values.exactPhrase}"`);
    if (values.anyWords) queryParts.push(`(${values.anyWords.split(" ").join(" OR ")})`);
    if (values.noneWords)
      queryParts.push(
        values.noneWords
          .split(" ")
          .map((w) => `-${w}`)
          .join(" ")
      );
    if (values.numbersFrom || values.numbersTo)
      queryParts.push(`${values.numbersFrom || ""}..${values.numbersTo || ""}`);

    // Narrow Results
    if (values.siteOrDomain) queryParts.push(`site:${values.siteOrDomain}`);
    if (values.fileType) queryParts.push(`filetype:${values.fileType}`);
    if (values.termsAppearing) {
      if (values.termsAppearing === "title") queryParts.unshift(`intitle:`);
      if (values.termsAppearing === "title") queryParts = queryParts.map((p) => `intitle:${p}`);
      else if (values.termsAppearing === "url") queryParts = queryParts.map((p) => `inurl:${p}`);
      else if (values.termsAppearing === "text") queryParts = queryParts.map((p) => `intext:${p}`);
    }

    return queryParts.join(" ");
  })();

  // Construct the full URL with query params for non-operator filters like language/region/date
  const fullUrl = (() => {
    const baseUrl = "https://www.google.com/search";
    const params = new URLSearchParams();
    params.set("q", queryPreview);

    if (values.language) params.set("lr", `lang_${values.language}`);
    if (values.region) params.set("cr", `country${values.region}`);
    if (values.lastUpdate) params.set("tbs", `qdr:${values.lastUpdate}`);

    return `${baseUrl}?${params.toString()}`;
  })();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(queryPreview);
    toast({
      title: "Copied to clipboard",
      description: "Search query copied to clipboard.",
    });
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="Google Advanced Search Generator"
        description="Master Google search with a visual interface. Filter by file type, domain, exact matches, numeric ranges, language, region, and more."
        url="https://plugmonkey.xyz/tool/google-advanced-search/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Google Advanced Search",
            url: "https://plugmonkey.xyz/tool/google-advanced-search/",
          },
        ]}
        faq={googleAdvancedSearchFAQ.map((item) => ({
          question: item.question,
          answer: item.answer,
        }))}
      />
      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-red-500/10 rounded-full text-red-500">
          <FaGoogle className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Google Advanced Search Generator</h1>
          <p className="text-muted-foreground mt-1">
            Master Google Fu without memorizing search operators.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          Google's search is powerful, but mastering advanced operators like 'site:', 'filetype:',
          'intitle:', and numeric ranges requires memorizing syntax. This tool makes it simple—build
          complex searches with filters for file types, domains, date ranges, language, region, and
          more, all through a clean interface.
        </p>
        <p>
          Perfect for researchers, students, and professionals who need to find specific content
          quickly. Search for PDFs, filter by domain (like .edu for academic sources), use numeric
          ranges for years or prices, or combine multiple filters to find exactly what you're
          looking for. Generate your search query, copy it, or jump straight to Google with your
          filters applied.
        </p>
        <p>
          No more guessing at search syntax. Build your query visually, see exactly what you're
          searching for, and get better results faster.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HiSearch /> Find Pages With...
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="gas-allWords">All these words</Label>
                <Input
                  id="gas-allWords"
                  {...register("allWords")}
                  placeholder="best chrome extensions"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gas-exactPhrase">This exact word or phrase</Label>
                <Input
                  id="gas-exactPhrase"
                  {...register("exactPhrase")}
                  placeholder="social media automation"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gas-anyWords">Any of these words</Label>
                <Input
                  id="gas-anyWords"
                  {...register("anyWords")}
                  placeholder="twitter x facebook"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gas-noneWords">None of these words</Label>
                <Input
                  id="gas-noneWords"
                  {...register("noneWords")}
                  placeholder="pinterest reddit"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gas-numbersFrom">Numbers ranging from</Label>
                  <Input id="gas-numbersFrom" {...register("numbersFrom")} placeholder="2020" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gas-numbersTo">To</Label>
                  <Input id="gas-numbersTo" {...register("numbersTo")} placeholder="2025" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HiFilter /> Narrow Results
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gas-language">Language</Label>
                  <select
                    id="gas-language"
                    {...register("language")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">Any Language</option>
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="it">Italian</option>
                    <option value="pt">Portuguese</option>
                    <option value="ru">Russian</option>
                    <option value="zh-CN">Chinese (Simplified)</option>
                    <option value="ja">Japanese</option>
                    <option value="ko">Korean</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gas-region">Region</Label>
                  <select
                    id="gas-region"
                    autoComplete="off"
                    {...register("region")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">Any Region</option>
                    <option value="US">United States</option>
                    <option value="GB">United Kingdom</option>
                    <option value="CA">Canada</option>
                    <option value="AU">Australia</option>
                    <option value="IN">India</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gas-lastUpdate">Last Update</Label>
                  <select
                    id="gas-lastUpdate"
                    {...register("lastUpdate")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">Anytime</option>
                    <option value="h">Past 24 hours</option>
                    <option value="w">Past week</option>
                    <option value="m">Past month</option>
                    <option value="y">Past year</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gas-siteOrDomain">Site or Domain</Label>
                  <Input
                    id="gas-siteOrDomain"
                    {...register("siteOrDomain")}
                    placeholder="nytimes.com or .edu"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gas-fileType">File Type</Label>
                  <select
                    id="gas-fileType"
                    {...register("fileType")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">Any Format</option>
                    <option value="pdf">Adobe Acrobat PDF (.pdf)</option>
                    <option value="ps">Adobe Postscript (.ps)</option>
                    <option value="kml">Google Earth (.kml)</option>
                    <option value="gp">Google Earth (.kmz)</option>
                    <option value="xls">Microsoft Excel (.xls)</option>
                    <option value="ppt">Microsoft PowerPoint (.ppt)</option>
                    <option value="doc">Microsoft Word (.doc)</option>
                    <option value="rtf">Rich Text Format (.rtf)</option>
                    <option value="swf">Shockwave Flash (.swf)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gas-termsAppearing">Terms Appearing</Label>
                  <select
                    id="gas-termsAppearing"
                    {...register("termsAppearing")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">Anywhere in the page</option>
                    <option value="title">In the title of the page</option>
                    <option value="text">In the text of the page</option>
                    <option value="url">In the URL of the page</option>
                    <option value="links">In links to the page</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-6">
            <Card className="bg-muted/50 border-primary/20 shadow-lg">
              <CardHeader>
                <CardTitle>Generated Query</CardTitle>
                <CardDescription>Ready to use on Google</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-background border rounded-md p-4 font-mono text-sm break-words min-h-[60px]">
                  {queryPreview || (
                    <span className="text-muted-foreground opacity-50">
                      Start typing to generate query...
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-3">
                  <Button
                    onClick={copyToClipboard}
                    variant="outline"
                    className="w-full"
                    disabled={!queryPreview}
                  >
                    <HiClipboardCopy className="mr-2 h-4 w-4" /> Copy Query
                  </Button>
                  <Button
                    onClick={() => window.open(fullUrl, "_blank")}
                    className="w-full bg-red-500 hover:bg-red-600 text-white"
                    disabled={!queryPreview}
                  >
                    Search on <FaGoogle className="mr-2 h-4 w-4" />
                    <HiExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="bg-card border rounded-xl p-6">
              <h2 className="font-bold text-lg mb-4">Prompt Faster with AI</h2>
              <p className="text-muted-foreground text-sm mb-4">
                Google is great, but AI is faster. Use <strong>Prompt Anything Pro</strong> to query
                AI models directly from any text on any page.
              </p>
              <Button asChild variant="secondary" className="w-full">
                <Link href="/product/prompt-anything-pro/">View Extension</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ
              items={googleAdvancedSearchFAQ.map((item) => ({
                question: item.question,
                answer: item.answer,
              }))}
              showSupportLink={false}
              className="space-y-4"
            />
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
